<template>
    <div class="filter-pop-wrap" ref="rootRef">
        <button
            type="button"
            class="filter-toggle-btn"
            :class="{ 'is-active': isOpen }"
            @click="onClickToggle"
        >
            <i class="fa-solid fa-filter"></i>
            <span v-if="activeCount" class="filter-count">{{ activeCount }}</span>
        </button>

        <transition name="filter-pop">
            <div v-if="isOpen" class="filter-pop" :style="{ width: popoverWidth }">
                <div class="filter-pop-head">
                    <span class="filter-pop-title">{{ title }}</span>
                    <button type="button" class="filter-pop-close" @click="close">
                        <i class="fa-solid fa-xmark"></i>
                    </button>
                </div>

                <div class="filter-pop-body">
                    <div class="row g-1">
                        <slot></slot>
                    </div>
                </div>

                <div class="filter-pop-foot">
                    <button type="button" class="btn btn-sm filter-clear-btn" @click="onClickClear">{{ clearTitle }}</button>
                    <button type="button" class="btn btn-sm btn-primary filter-apply-btn" @click="onClickApply">{{ applyTitle }}</button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'

const props = defineProps({
    title: {
        type: String,
        default: 'Filters'
    },
    applyTitle: {
        type: String,
        default: 'Apply'
    },
    clearTitle: {
        type: String,
        default: 'Clear'
    },
    activeCount: {
        type: Number,
        default: 0
    },
    popoverWidth: {
        type: String,
        default: '520px'
    },
    closeOnApply: {
        type: Boolean,
        default: true
    }
})

const emit = defineEmits(['apply', 'clear', 'open', 'close'])

const rootRef = ref(null)
const isOpen = ref(false)

const open = () => {
    isOpen.value = true
    emit('open')
}

const close = () => {
    if (! isOpen.value) return
    isOpen.value = false
    emit('close')
}

const onClickToggle = () => {
    if (isOpen.value) return close()
    open()
}

const onClickApply = () => {
    emit('apply')
    if (! props.closeOnApply) return
    close()
}

const onClickClear = () => {
    emit('clear')
    if (! props.closeOnApply) return
    close()
}

const onDocumentMouseDown = (event) => {
    if (! isOpen.value) return
    if (! rootRef.value) return
    if (rootRef.value.contains(event.target)) return
    if (event.target.closest('.vs__dropdown-menu')) return
    close()
}

const onDocumentKeydown = (event) => {
    if (! isOpen.value) return
    if (event.key !== 'Escape') return
    close()
}

onMounted(() => {
    document.addEventListener('mousedown', onDocumentMouseDown)
    document.addEventListener('keydown', onDocumentKeydown)
})

onUnmounted(() => {
    document.removeEventListener('mousedown', onDocumentMouseDown)
    document.removeEventListener('keydown', onDocumentKeydown)
})

defineExpose({
    open,
    close,
    isOpen
})
</script>

<style scoped>
.filter-pop-wrap {
    position: relative;
}
.filter-toggle-btn {
    position: relative;
    width: 38px;
    height: 38px;
    border: 1px solid #e4e7ec;
    border-radius: 10px;
    background: #fff;
    color: #667085;
    cursor: pointer;
    transition: all .15s ease;
}
.filter-toggle-btn:hover {
    background: #f9fafb;
    color: #475467;
}
.filter-toggle-btn.is-active {
    border-color: #7367f0;
    color: #7367f0;
    background: #f4f3ff;
}
.filter-count {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: #7367f0;
    color: #fff;
    font-size: 10px;
    font-weight: 600;
    line-height: 18px;
}
.filter-pop {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    z-index: 1050;
    background: #fff;
    border: 1px solid #eaecf0;
    border-radius: 14px;
    box-shadow: 0 12px 32px rgba(16, 24, 40, .14);
    overflow: visible;
}
.filter-pop-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px 10px;
}
.filter-pop-title {
    font-size: 13px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #344054;
}
.filter-pop-close {
    border: 0;
    background: transparent;
    color: #98a2b3;
    font-size: 14px;
    line-height: 1;
    padding: 4px;
    cursor: pointer;
}
.filter-pop-close:hover {
    color: #475467;
}
.filter-pop-body {
    padding: 0 18px 6px;
}
.filter-pop-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 18px 14px;
    border-top: 1px solid #f2f4f7;
}
.filter-clear-btn {
    background: #f2f4f7;
    color: #667085;
    border-radius: 8px;
    min-width: 72px;
}
.filter-clear-btn:hover {
    background: #e4e7ec;
    color: #475467;
}
.filter-apply-btn {
    border-radius: 8px;
    min-width: 82px;
}
.filter-pop :deep(.filter-label) {
    display: block;
    margin-bottom: 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #101828;
}
.filter-pop :deep(.col-12),
.filter-pop :deep([class*="col-sm-"]) {
    margin-bottom: 10px;
}
.filter-pop :deep(.vs__dropdown-toggle) {
    border-radius: 8px;
    border-color: #e4e7ec;
}
.filter-pop :deep(.vs__dropdown-menu) {
    z-index: 1060;
}
.filter-pop-enter-active,
.filter-pop-leave-active {
    transition: opacity .16s ease, transform .16s ease;
}
.filter-pop-enter-from,
.filter-pop-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}
@media (max-width: 576px) {
    .filter-pop {
        width: calc(100vw - 2rem) !important;
        right: -.5rem;
    }
}
</style>
